import React, { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { formatDate } from '../../utils/dateHelpers';
import { getValueRangeLabel, formatActualValue } from '../forms/OppModal';

const stages = [
  "New",
  "Qualified",
  "Not Qualified",
  "Proposal Sent",
  "Closed Won",
  "Closed Lost"
];

// Stage-based color schemes for better visual organization
const getStageColors = (stage) => {
  switch (stage) {
    case "New":
      return {
        background: "bg-teal-50",
        border: "border-teal-200",
        header: "text-teal-800",
        headerBg: "bg-teal-100"
      };
    case "Qualified":
      return {
        background: "bg-indigo-50",
        border: "border-indigo-200", 
        header: "text-indigo-800",
        headerBg: "bg-indigo-100"
      };
    case "Not Qualified":
      return {
        background: "bg-yellow-50",
        border: "border-yellow-200",
        header: "text-yellow-800",
        headerBg: "bg-yellow-100"
      };
    case "Proposal Sent":
      return {
        background: "bg-purple-50",
        border: "border-purple-200",
        header: "text-purple-800",
        headerBg: "bg-purple-100"
      };
    case "Closed Won":
      return {
        background: "bg-green-50",
        border: "border-green-200",
        header: "text-green-800",
        headerBg: "bg-green-100"
      };
    case "Closed Lost":
      return {
        background: "bg-red-50",
        border: "border-red-200",
        header: "text-red-800", 
        headerBg: "bg-red-100"
      };
    default:
      return {
        background: "bg-gray-50",
        border: "border-gray-200",
        header: "text-gray-800",
        headerBg: "bg-gray-100"
      };
  }
};

const PipelineBoard = ({ opportunities, onOpportunityClick, onStageChange }) => {
  const [draggedOpp, setDraggedOpp] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState({}); // Track how many to show per stage
  const { currentUser } = useAuth();
  
  const PAGE_SIZE = 5; // Show 5 opportunities per page

  // Load more opportunities for a stage
  const loadMore = (stage) => {
    setVisibleCounts(prev => ({
      ...prev,
      [stage]: (prev[stage] || PAGE_SIZE) + PAGE_SIZE
    }));
  };

  // Reset to initial view for a stage
  const showLess = (stage) => {
    setVisibleCounts(prev => ({
      ...prev,
      [stage]: PAGE_SIZE
    }));
  };

  // Group and sort opportunities by stage (sorted by due date ascending - closest to today first)
  const grouped = {};
  stages.forEach(stage => { grouped[stage] = []; });
  opportunities.forEach(opp => {
    const stage = stages.find(s => s.toLowerCase() === (opp.stage || '').toLowerCase());
    if (stage) grouped[stage].push(opp);
  });
  
  // Sort each stage by due date (ascending - closest dates first)
  stages.forEach(stage => {
    grouped[stage].sort((a, b) => {
      const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      return dateA - dateB;
    });
  });

  return (
    <div className="flex space-x-4 overflow-x-auto pb-2">
      {stages.map(stage => {
        const colors = getStageColors(stage);
        const allOpps = grouped[stage];
        const oppCount = allOpps.length;
        const currentVisible = visibleCounts[stage] || PAGE_SIZE;
        const visibleOpps = allOpps.slice(0, currentVisible);
        const hasMore = oppCount > currentVisible;
        const canShowLess = currentVisible > PAGE_SIZE && oppCount > PAGE_SIZE;
        
        return (
          <div
            key={stage}
            className={`${colors.background} ${colors.border} border rounded-lg p-4 min-w-[250px] flex-1 transition-colors duration-150`}
            onDragOver={e => e.preventDefault()}
            onDrop={e => {
              e.preventDefault();
              if (draggedOpp && draggedOpp.stage !== stage && onStageChange) {
                onStageChange(draggedOpp, stage);
                setDraggedOpp(null);
              }
            }}
          >
            <div className={`${colors.headerBg} rounded-md px-3 py-2 mb-3`}>
              <div className="flex items-center justify-between">
                <h3 className={`font-bold text-sm ${colors.header}`}>{stage}</h3>
                <span className={`text-xs ${colors.header} opacity-75 font-medium`}>
                  {oppCount}
                </span>
              </div>
            </div>
            <div className="flex flex-col space-y-2 min-h-[40px]">
              {visibleOpps.map(opp => {
              // Format due date
              let dueDate = '-';
              if (opp.dueDate) {
                try {
                  const d = new Date(opp.dueDate);
                  dueDate = !isNaN(d) ? formatDate(d, currentUser?.dateFormat) : '-';
                } catch {
                  dueDate = '-';
                }
              }

              return (
                <div key={opp.id} className="w-full">
                  <div
                    className="w-full bg-white rounded shadow p-2 cursor-pointer hover:bg-indigo-50 border hover:border-indigo-200 transition-all duration-150"
                    draggable
                    onDragStart={() => setDraggedOpp(opp)}
                    onDragEnd={() => setDraggedOpp(null)}
                    onClick={() => onOpportunityClick?.(opp)}
                  >
                  {/* Opportunity Number */}
                  <div className="font-mono text-xs text-indigo-700 font-bold mb-1">
                    {opp.opportunityNumberLabel || '-'}
                  </div>
                  {/* Lead Source */}
                  <div className="text-xs text-gray-500 mb-1">{opp.leadSource || '-'}</div>
                  {/* Customer Name */}
                  <div className="text-sm font-semibold">{opp.customerName || '-'}</div>
                  {/* Sales Rep */}
                  <div className="text-xs text-gray-500 mb-1">
                    Sales Rep: {opp.assignedUserName || '-'}
                  </div>
                  {/* Value and Due Date */}
                  <div className="flex justify-between text-xs text-gray-600">
                    <span>{opp.actualValue ? formatActualValue(opp.actualValue) : getValueRangeLabel(opp.value)}</span>
                    <span className={
                      dueDate !== '-' && opp.dueDate && (() => {
                        const due = new Date(opp.dueDate);
                        const today = new Date();
                        due.setHours(0,0,0,0);
                        today.setHours(0,0,0,0);
                        const stage = (opp.stage || '').toLowerCase();
                        return due <= today && stage !== 'closed won' && stage !== 'closed lost';
                      })()
                        ? "font-bold text-red-600"
                        : ""
                    }>
                      {dueDate}
                    </span>
                  </div>
                </div>
                </div>
              );
            })}
          </div>
          
          {/* Load More Button */}
          {hasMore && (
            <button
              onClick={() => loadMore(stage)}
              className={`mt-3 w-full py-2 px-3 rounded-md text-xs font-medium transition-colors duration-150 ${colors.headerBg} ${colors.header} hover:opacity-80`}
            >
              Load More ({Math.min(PAGE_SIZE, oppCount - currentVisible)} more) <span className="ml-1">▼</span>
            </button>
          )}
          
          {/* Show Less Button */}
          {canShowLess && (
            <button
              onClick={() => showLess(stage)}
              className={`mt-2 w-full py-2 px-3 rounded-md text-xs font-medium transition-colors duration-150 ${colors.headerBg} ${colors.header} hover:opacity-80`}
            >
              Show Less <span className="ml-1">▲</span>
            </button>
          )}
        </div>
        );
      })}
    </div>
  );
};

export default PipelineBoard;

